import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FieldConditionDto,
  FileUploadResult,
  FormQuestionsMeta,
  TemplateVariable,
} from '@app/+authenticated/+employees/templates/contract-template/models/contract-template.model';
import { Store } from '@ngrx/store';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { EmployeeService } from '@reducers/orm/employee/employee.service';
import { getTeamEntities } from '@reducers/orm/team/team.service';
import { ApiGateway, objectToSearchParams } from '@sb/shared/api';
import { hasPermission } from '@sb/shared/permission/util';
import { IGNORE_META_MESSAGE } from '@sb/shared/util';
import { SelectItem } from '@sb/ui/select';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import {
  AddNewHirePayload,
  HireSectionStatus,
  NewHire,
  NewHireFormCompletion,
  NewHireListItem,
  NewHireVariables,
  OnboardingData,
  SigningEmployeePayload,
} from '../models/new-hires.model';
import { idToSeed } from './id-to-seed.helper';

@Injectable({ providedIn: 'root' })
export class NewHiresService {
  private api: ApiGateway = inject(ApiGateway);
  #employeeService = inject(EmployeeService);
  #store = inject<Store<AppState>>(Store);
  #teams = this.#store.selectSignal(getTeamEntities);
  #permissionState = this.#store.selectSignal(getPermissionState);

  getEmploymentCategories() {
    return this.api.get<SelectItem[]>('hiring/employment-categories');
  }

  uploadIdDocuments(token: string, data: File) {
    return this.api.post<FileUploadResult>(`hiring/hire/documents/${token}`, data, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  /**
   * Complete the hiring process and convert to employee, then hand back the page to open next.
   * This endpoint currently handles both new hires and rehires
   * - New hires: Creates a new employee record
   * - Rehires: Reactivates existing employee and creates new contract
   */
  private completeHiringProcess(newHireId: string): Observable<string[]> {
    const newHiresRoute = ['/hiring/new-hires'];

    return this.api.post<NewHire>(`hiring/hire/${newHireId}/hired`, {}).pipe(
      switchMap((newHire) => {
        const employeeId = newHire.employee_id;
        if (!employeeId || !this.#canViewEmployee(newHire)) {
          return of(newHiresRoute);
        }

        // The employee profile guard only opens employees that are already in the store.
        // A failed load must not fail the activation, the hire is already hired by then.
        return this.#employeeService.fetch(employeeId).pipe(
          map(() => ['/employees', employeeId]),
          catchError(() => of(newHiresRoute)),
        );
      }),
    );
  }

  // Managing new hires does not need 'View all users', and loading a profile without it fails with a permission toast.
  #canViewEmployee(newHire: NewHire): boolean {
    const departmentId = newHire.team_id ? this.#teams()[newHire.team_id]?.department_id : undefined;
    if (!departmentId) return false;

    return hasPermission({ permissions: ['View all users'], departments: [departmentId] }, this.#permissionState());
  }

  convertNewHireToEmployee(newHireId: string) {
    return this.completeHiringProcess(newHireId);
  }

  reactivateRehiredEmployee(newHireId: string) {
    return this.completeHiringProcess(newHireId);
  }

  downloadIdDocuments(newHireId: string, documentPath: string) {
    return this.api.download(`hiring/hire/${newHireId}/secure-document/${documentPath}`);
  }

  generateContract(newHireId: string) {
    return this.api.download(`hiring/hire/${newHireId}/contract`);
  }

  getContractPreview(newHireId: string) {
    return this.api.getBlob(`hiring/hire/${newHireId}/contract/preview`, { headers: { ignoreMetaMessage: true } });
  }

  downloadSignedContract(newHireId: string) {
    return this.api.download(`hiring/hire/${newHireId}/digital-signature/contract`);
  }

  getEsignatureState(newHireId: string) {
    return this.api.get(`hiring/hire/${newHireId}/digital-signature`);
  }

  completeNewHireInformation(token: string, data: NewHireFormCompletion) {
    return this.api.post(`hiring/hire/formcompletion/${token}`, data, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  checkTokenIsValid(token: string) {
    return this.api.get(`hiring/hire/formcompletion/${token}`, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  getFormQuestions(token: string, locale?: string) {
    // `meta: true` keeps the full envelope; ApiGateway would otherwise unwrap
    // `data` and drop `meta.sections`. The visibility cascade is field-based, so
    // each section condition is copied onto the `category` of its fields.
    return this.api
      .get<{ data: TemplateVariable[]; meta?: FormQuestionsMeta }>(`hiring/hire/formcompletion/${token}/questions`, {
        params: objectToSearchParams({ locale }),
        meta: true,
      })
      .pipe(map((response) => this.#attachSectionConditions(response.data ?? [], response.meta?.sections)));
  }

  #attachSectionConditions(questions: TemplateVariable[], sections: FormQuestionsMeta['sections']): TemplateVariable[] {
    const conditionByKey = new Map<string, FieldConditionDto>();
    for (const section of sections ?? []) {
      if (section.condition) {
        conditionByKey.set(section.key, section.condition);
      }
    }

    if (conditionByKey.size === 0) return questions;

    return questions.map((field) => {
      const condition = conditionByKey.get(field.category.key);
      return condition ? { ...field, category: { ...field.category, condition } } : field;
    });
  }

  getOnboardingData(token: string) {
    // Non-critical: the form still builds without the guardian flag (it defaults off), so a
    // failure here must not raise a global error toast — the page-level token check
    // (#checkTokenIsValid) owns the "invalid token" messaging for the form.
    return this.api.get<OnboardingData>(`hiring/hire/onboarding-data/${token}`, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  resendInvite(newHireId: string) {
    return this.api.post(`hiring/hire/${newHireId}/invite`, {});
  }

  updateNewHireContract(newHireId: string, data?: NewHireVariables | { template_id: string }) {
    return this.api.put(`hiring/hire/${newHireId}/dynamic-form`, data);
  }

  saveNewHireContract(newHireId: string, data?: Record<string, unknown> | { template_id: string }) {
    return this.api
      .post(`hiring/hire/${newHireId}/dynamic-form`, data)
      .pipe(map((newHire) => this.adaptNewHire(newHire)));
  }

  getNewHireVariables(newHireId: string) {
    return this.api.get(`hiring/hire/${newHireId}/dynamic-form`);
  }

  // The indicator hides itself when this fails, so an error here must not raise the toast that
  // suggests the hire itself failed to load.
  getHireSections(newHireId: string) {
    return this.api.get<HireSectionStatus[]>(`hiring/hire/${newHireId}/sections`, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  updateNewHire(newHireId: string, data: Partial<NewHireVariables>) {
    return this.api.put(`hiring/hire/${newHireId}`, data).pipe(map((newHire) => this.adaptNewHire(newHire)));
  }

  // SHB-19015: Remove this method when TMP_HR_PLUS_SETTINGS is permanently enabled.
  updateSensitiveInformation(newHireId: string, includeSensitiveInfo: boolean) {
    return this.api.patch(
      `hiring/hire/${newHireId}`,
      { include_sensitive_info: includeSensitiveInfo },
      {
        context: new HttpContext().set(IGNORE_META_MESSAGE, true),
      },
    );
  }

  // Errors are surfaced inline by the hire/rehire modals (handleHireSubmitError),
  // so these requests opt out of the global error toast.
  addNewHire(payload: AddNewHirePayload) {
    return this.api.post('hiring/hire', payload, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  reHire(payload: AddNewHirePayload) {
    return this.api.post('hiring/hire', payload, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  selectSigningEmployee(newHireId: string | undefined, data: SigningEmployeePayload) {
    return this.api.post(`hiring/hire/${newHireId}/digital-signature`, data, {
      context: new HttpContext().set(IGNORE_META_MESSAGE, true),
    });
  }

  getNewHireById(newHireId: string) {
    return this.api.get(`hiring/hire/${newHireId}`).pipe(map((newHire) => this.adaptNewHire(newHire)));
  }

  getAllNewHires() {
    return this.api.get('hiring/hire').pipe(map((result) => this.adaptNewHires(result)));
  }

  deleteNewHire(newHireId: string) {
    return this.api.delete(`hiring/hire/${newHireId}`);
  }

  stopHiringProcess(newHireId: string) {
    return this.api.post(`hiring/hire/${newHireId}/stop`, {}).pipe(map((newHire) => this.adaptNewHire(newHire)));
  }

  cancelDigitalSignature(newHireId: string, reason: 'contractualization_aborted' | 'errors_in_document') {
    return this.api.post(`hiring/hire/${newHireId}/digital-signature/cancel`, { reason });
  }

  private adaptNewHire(newHire: NewHire): NewHire {
    const variables = {
      ...newHire.variables,
      team_id: newHire.team_id,
      template_id: newHire.template_id,
      contract_type_id: newHire.contract_type_id,
    };

    const guardianSignatureCount = this.#guardianSignatureCount(newHire);

    return {
      ...newHire,
      variables,
      // The guardian count is persisted as a hire variable ('0'/'1'/'2'); surface it as the
      // top-level number the edit form + contract-template filter read, plus the derived
      // boolean for any not-yet-migrated reader.
      guardian_signature_count: guardianSignatureCount,
      requires_guardian_signature: guardianSignatureCount > 0,
    };
  }

  /**
   * Resolves the guardian-signature count for a hire, preferring the new
   * guardian_signature_count variable and falling back to the legacy
   * requires_guardian_signature boolean for hires created before the count migration.
   */
  #guardianSignatureCount(newHire: NewHire): number {
    if (newHire.guardian_signature_count !== undefined) {
      // Coerce + clamp the top-level value too (same 0–2 contract as the variables path below).
      const parsed = Number(newHire.guardian_signature_count);
      return Number.isFinite(parsed) ? Math.max(0, Math.min(Math.trunc(parsed), 2)) : 0;
    }

    // variables is [key: string]: unknown, so guard against non-numeric values (NaN) and clamp to the
    // supported 0–2 range; fall through to the legacy boolean when the value can't be parsed.
    const rawCount = newHire.variables['guardian_signature_count'];
    if (rawCount !== undefined && rawCount !== null && rawCount !== '') {
      const parsed = typeof rawCount === 'number' ? rawCount : Number(rawCount);
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.min(Math.trunc(parsed), 2));
      }
    }

    const legacy = newHire.requires_guardian_signature ?? newHire.variables['requires_guardian_signature'] === '1';

    return legacy ? 1 : 0;
  }

  private adaptNewHires(newHires: NewHire[]): NewHireListItem[] {
    return newHires.map((newHire) => {
      const { first_name, prefix_name, middle_name, last_name } = newHire.variables;
      const name = [first_name, prefix_name, middle_name, last_name].filter(Boolean).join(' ');

      return {
        ...newHire,
        name,
        seed: idToSeed(newHire.id),
      };
    });
  }
}
